import { Box, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { useField, FormikValues, useFormikContext } from 'formik';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import {
  Step,
  Icon,
  Utils,
  FormCard,
  useAlert,
  isMobile,
  FlexLayout,
  useNavigate,
  ActionQueue,
  ButtonColors,
  FormCardTypes,
  SingleOptions,
  FormCardFields,
  FormCardRender,
  StandardButton,
  ValidationType,
  handleMutation,
  ActionQueueType,
  FormOptionsType,
  StepInstructions,
} from '@gv/triage-components';

import { Routes } from 'config';
import { useAppDispatch } from 'store';
import { clientTypeOptions } from 'types/data';
import { RootState, useAppSelector } from 'store';
import { useUpdatePetMutation } from 'store/api/clients';
import { hasNoneOption } from 'hooks/dynamic-form/helpers';
import { PimsInfo } from 'components/cases/form/pims-info';
import { GreetingInfo } from 'components/cases/form/greeting';
import { joinRoutes, scrollCaseToError } from 'utils/helpers';
import { ClientField } from 'components/cases/form/client-field';
import { useGetHospitalQuery } from 'store/api/hospital-bitwerx';
import { setCurrentTaskClient } from 'store/api/action-queue/thunks';
import { useDetailsQuery as useClientDetailsQuery } from 'store/api/clients';
import { useConnectionState } from 'pages/action-center/comm/context/connection-states';
import {
  selectHasInQueue,
  selectCurrentTask,
} from 'store/api/action-queue/selectors';
import {
  useFormEntities,
  DepartmentSelect,
  callOutcomeModel,
} from 'components/cases/form';
import {
  TextOutStep,
  CallOutcomeType,
  PetOwnerTemplate,
  MessageRecipientType,
} from 'types';
import {
  useSpeciesProps,
  useDynamicOptions,
  useOptionalHospitalData,
  useSchedulingMatrixProps,
} from 'hooks';
import {
  ClientTasksList,
  useClientTasksProps,
  AppointmentScheduled,
  SchedulingMatrixTable,
} from 'components';
import {
  useReferToPPH,
  useReferToOnCall,
  useReferToOtherEr,
  useCorporateOptions,
  useReferToCurrentEr,
  useSchedulingPaymentForm,
} from 'hooks/refer';

import { useOutcomeRulesValues } from '../useOutcomeRulesValues';
import { getPayload } from '../../../pages/hospitals/details/clients/form/config';

// import { Aside } from './aside';
import * as Styles from './styles';
import { SubmitStep } from './submitStep';
import { DynamicCaseFormProps } from './types';
import {
  hasFieldType,
  getNextStepId,
  getNextOutcomeId,
  getSpeciesStepId,
  getShowDepartmentSelect,
  getDepartmentsSelectPath,
  getCorporateOptionsByOutcome,
} from './helpers';

const {
  Helpers: { fillObjectByPath },
  Object: { getValueByPath, shallowDifference },
} = Utils;

const {
  Cases: { Details },
  ActionCenter: { Comm, InComm },
} = Routes;
import { useCreateMutation } from 'store/api/clients';

export const CaseDynamicForm = ({
  steps,
  isEdit,
  setDVM,
  isLoading,
  isTesting,
  activeStep,
  setService,
  hideFooter,
  setPayment,
  setReferral,
  onPlaceBack,
  stepQuestions,
  setActiveStep,
  onSubmitPress,
  setPassedSteps,
  onTransferMerge,
  setPaymentLinks,
  passedSteps = [],
  setValidationFields,
}: DynamicCaseFormProps) => {
  const {
    values,
    resetForm,
    submitForm,
    validateForm,
    isSubmitting,
    initialValues,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<FormikValues>();
  const [create, mutation] = useCreateMutation();
  handleMutation({ ...mutation });
  const dispatch = useAppDispatch();
  const { hospitalId } = useFormEntities();
  const mobile = isMobile();
  const { hospital } = useOptionalHospitalData(hospitalId);
  const clientTasksProps = useClientTasksProps(isTesting);
  const dynamicOptions = useDynamicOptions({ isTesting });
  const referToPPHProps = useReferToPPH({ setService });
  const referToOtherErProps = useReferToOtherEr({ setReferral });
  const referToCurrentErProps = useReferToCurrentEr({ setReferral });
  const referToOnCallProps = useReferToOnCall({ setDVM });
  const { matricesList, matrixConfig } = useSchedulingMatrixProps(hospitalId);
  const { acceptableSpecies } = useSpeciesProps(hospitalId);
  const { handleError } = useAlert();
  const { ruleValues } = useOutcomeRulesValues({
    acceptance: referToCurrentErProps.acceptance,
    onCallDocsCount: referToOnCallProps.onCallDocProps.docsOptions?.length,
  });
  const { corporateGroupOptions, isCorporateGroupFetching } =
    useCorporateOptions(hospitalId);
  const { queryProps, isActiveHospitalPayment } = useSchedulingPaymentForm();

  const task = useAppSelector(selectCurrentTask);
  const { type } = task ?? {};

  const [updatePet, updateMutation] = useUpdatePetMutation();
  handleMutation({ ...updateMutation });

  const basePath = joinRoutes([Comm, Details.Consultation.Index], {});
  const addPetPath = joinRoutes([basePath, InComm.NewPet], {});
  const editPetPath = joinRoutes([basePath, 'pet'], {});
  const editClientPath = joinRoutes([basePath, InComm.EditClient], {});
  const medicalRecordsPath = `${basePath}${InComm.PetMedicalRecords}`;

  const isChatType = useMemo(() => type !== ActionQueueType.Voice, [type]);

  const [callOutComeTypes] = useField(callOutcomeModel.type);
  const isEmergency = callOutComeTypes.value === CallOutcomeType.Emergency;
  const isAppointment = callOutComeTypes.value === CallOutcomeType.Appointment;

  const currentActiveStep = useMemo(
    () => (activeStep ? steps[activeStep] : undefined),
    [activeStep, steps]
  );
  const { isFinal, outcome, subOutcome } = currentActiveStep ?? {};
  const isSubmitStep = !!isFinal;

  const widgetData = useMemo<Record<string, any>>(
    () => ({
      [FormCardTypes.Toxicity]: referToPPHProps,
      [FormCardTypes.OnCallDoc]: referToOnCallProps,
      [FormCardTypes.ReferToER]: referToCurrentErProps,
      [ValidationType.ReferToOtherER]: referToOtherErProps,
    }),
    [
      referToPPHProps,
      referToOnCallProps,
      referToOtherErProps,
      referToCurrentErProps,
    ]
  );

  const getFieldOptions = useCallback(
    (field: FormCardFields) => {
      const copy = { ...field };
      if (
        copy?.checkboxProps?.name === 'isNumberConfirm' ||
        copy?.checkboxProps?.name === 'isNameConfirm'
      ) {
        copy.checkboxProps = null;
      }

      const { optionsType, formFieldProps, type: fieldType } = field;
      if (fieldType === FormCardTypes.PetInfo) {
        copy.editPetPath = editPetPath;
        copy.medicalRecordsPath = medicalRecordsPath;
      }
      if (optionsType) {
        copy.formFieldProps = {
          ...formFieldProps,
          ...(dynamicOptions[optionsType as FormOptionsType] ?? {}),
        };
      }
      if (hasNoneOption(formFieldProps?.name)) {
        copy.withNoneOption = true;
      }
      return copy;
    },
    [editPetPath, JSON.stringify(dynamicOptions)]
  );

  const { data: client } = useClientDetailsQuery(
    { id: String(values?.client_id) },
    { skip: !values?.client_id }
  );
  const [bitwerxStatus, setBitwerxStatus] = useState<boolean>(false);
  const [hospitalEditStatus, setHospitalEditStatus] = useState<boolean>(false);
  const [hasAutoSelectedPet, setHasAutoSelectedPet] = useState<boolean>(false);
  const prevPassedStepsLengthRef = useRef(passedSteps.length);
  const [noneFieldName, setNoneFieldName] = useState<boolean>(false);
  useEffect(() => {
    const clientPets = values?.clientPets;

    if (clientPets && clientPets.length > 0 && !hasAutoSelectedPet) {
      const currentPets = values?.pets || [];
      const hasExistingSelection = currentPets.length > 0;
      if (!hasExistingSelection) {
        const firstPet = clientPets[0];
        if (firstPet?.id) {
          setFieldValue('pets', [firstPet.id]);
          setFieldValue('petIds', [firstPet.id]);
          setHasAutoSelectedPet(true);
        }
      }
    }
  }, [values?.clientPets, values?.pets, setFieldValue, hasAutoSelectedPet]);

  const processStepData = useCallback(
    (stepData: Step) => {
      return {
        ...stepData,
        widgets: stepData?.widgets?.map((widget) => {
          const showClientTasks = widget.id === SingleOptions.CancelAppointment;
          const isAppointmentScheduled =
            widget.id === SingleOptions.AppointmentDate;
          const additionalData = widget?.widgetType
            ? (widgetData[widget.widgetType] ?? {})
            : {};

          return {
            ...widget,
            ...additionalData,
            clientTasksList: showClientTasks ? (
              <ClientTasksList {...clientTasksProps} />
            ) : undefined,
            schedulingMartix: hasFieldType(
              FormCardTypes.SchedulingMatrix,
              widget
            ) ? (
              <SchedulingMatrixTable
                widgetView
                horizontalScroll
                matrices={matricesList}
                matrixConfig={matrixConfig}
              />
            ) : undefined,
            customNode: isAppointmentScheduled ? (
              <FlexLayout gap={16} flexDirection="column">
                <AppointmentScheduled
                  hideNotes
                  hideCheckProtocols
                  setPayment={setPayment}
                  hospitalPrices={queryProps.data}
                  clientTypeOptions={clientTypeOptions}
                  isCorporateFetching={isCorporateGroupFetching}
                  isActiveHospitalPayment={isActiveHospitalPayment}
                  corporateOptions={corporateGroupOptions?.scheduled}
                />
              </FlexLayout>
            ) : undefined,
            questions: widget?.questions?.map((question) => {
              const isPet = question.validationType === ValidationType.PetInfo;
              const isClient =
                question.validationType === ValidationType.ClientInfo;
              return {
                ...question,
                isEdit: isClient ? true : false,
                isBitwerxEnabled: bitwerxStatus,
                fields: question?.fields?.map((field: FormCardFields) =>
                  getFieldOptions(field)
                ),
                requiredFields: [
                  ...(question?.requiredFields ?? []),
                  ...(additionalData.requiredFields ?? []),
                ],
                addPath: isEdit
                  ? ''
                  : bitwerxStatus && client?.bitwerx_client_id
                    ? ''
                    : isClient
                      ? editClientPath
                      : isPet && hospital?.type !== 2
                        ? addPetPath
                        : '',
                viewPimsDetail:
                  bitwerxStatus && client?.bitwerx_client_id && isClient
                    ? `${basePath}${Routes.ActionCenter.InComm.BitwerxDetail}`.replace(
                        ':clientId',
                        client?.bitwerx_client_id
                      ) + ''
                    : '',
              };
            }),
          };
        }),
      };
    },
    [
      widgetData,
      clientTasksProps,
      matricesList,
      matrixConfig,
      queryProps.data,
      clientTypeOptions,
      isCorporateGroupFetching,
      isActiveHospitalPayment,
      corporateGroupOptions?.scheduled,
      bitwerxStatus,
      getFieldOptions,
      isEdit,
      editClientPath,
      addPetPath,
      hospital?.type,
      client?.bitwerx_client_id,
      basePath,
    ]
  );
  // const isSinglePageDynamicFlow =
  //   process.env.REACT_APP_SINGLE_PAGE_DYANMIC_FLOW;
  // const isShowSinglePageDynamicFlow = isSinglePageDynamicFlow
  //   ? JSON.parse(isSinglePageDynamicFlow).some(
  //       (item: any) => item == hospitalId
  //     )
  //   : false;
  const isShowSinglePageDynamicFlow = true;
  const { data: hospitalBitwerx } = useGetHospitalQuery({
    id: task?.h_id ?? 0,
  });
  const getBitwerxStatus = async (response: any) => {
    if (response?.response?.bitwerx_practice_id) {
      setBitwerxStatus(true);
    }
    if (response?.response?.is_hospital_edit_enabled === true) {
      setHospitalEditStatus(true);
    }
  };
  useEffect(() => {
    getBitwerxStatus(hospitalBitwerx);
  }, [hospitalBitwerx]);
  const currentStep = useMemo<Step | undefined>(
    () =>
      currentActiveStep && {
        ...currentActiveStep,
        widgets: currentActiveStep?.widgets?.map((widget) => {
          const showClientTasks = widget.id === SingleOptions.CancelAppointment;
          const isAppointmentScheduled =
            widget.id === SingleOptions.AppointmentDate;
          const additionalData = widget?.widgetType
            ? (widgetData[widget.widgetType] ?? {})
            : {};

          return {
            ...widget,
            ...additionalData,
            clientTasksList: showClientTasks ? (
              <ClientTasksList {...clientTasksProps} />
            ) : undefined,
            schedulingMartix: hasFieldType(
              FormCardTypes.SchedulingMatrix,
              widget
            ) ? (
              <SchedulingMatrixTable
                widgetView
                horizontalScroll
                matrices={matricesList}
                matrixConfig={matrixConfig}
              />
            ) : undefined,
            customNode: isAppointmentScheduled ? (
              <FlexLayout gap={16} flexDirection="column">
                <AppointmentScheduled
                  hideNotes
                  hideCheckProtocols
                  setPayment={setPayment}
                  hospitalPrices={queryProps.data}
                  clientTypeOptions={clientTypeOptions}
                  isCorporateFetching={isCorporateGroupFetching}
                  isActiveHospitalPayment={isActiveHospitalPayment}
                  corporateOptions={corporateGroupOptions?.scheduled}
                />
              </FlexLayout>
            ) : undefined,
            questions: widget?.questions?.map((question) => {
              const isPet = question.validationType === ValidationType.PetInfo;
              const isClient =
                question.validationType === ValidationType.ClientInfo;
              return {
                ...question,
                viewPimsDetail: '',
                isEdit: isClient ? true : false,
                isBitwerxEnabled: bitwerxStatus,
                fields: question?.fields?.map((field: FormCardFields) =>
                  getFieldOptions(field)
                ),
                requiredFields: [
                  ...(question?.requiredFields ?? []),
                  ...(additionalData.requiredFields ?? []),
                ],
                addPath: isEdit
                  ? ''
                  : bitwerxStatus && client?.bitwerx_client_id
                    ? ''
                    : isClient
                      ? editClientPath
                      : isPet && hospital?.type !== 2
                        ? addPetPath
                        : '',
              };
            }),
          };
        }),
      },
    [
      isEdit,
      setPayment,
      matricesList,
      matrixConfig,
      queryProps.data,
      getFieldOptions,
      currentActiveStep,
      clientTasksProps.data,
      isActiveHospitalPayment,
      isCorporateGroupFetching,
      JSON.stringify(widgetData),
      corporateGroupOptions?.scheduled,
    ]
  );

  const activeStepIndex = useMemo(
    () => passedSteps.findIndex((item) => item.id === activeStep),
    [activeStep, passedSteps]
  );

  const showDepartmentSelect = useMemo(
    () => getShowDepartmentSelect({ hospital, selectedStep: currentStep }),
    [hospital, currentStep]
  );

  const isClientPage = useMemo(
    () =>
      currentStep?.widgets?.some(
        (widget) => widget.widgetType === FormCardTypes.ClientInfo
      ),
    [currentStep]
  );

  useEffect(() => {
    if (
      bitwerxStatus &&
      client &&
      !client?.bitwerx_client_id &&
      isClientPage &&
      !hospitalEditStatus
    ) {
      handleError(
        'Please update the client and pet information to ensure the latest details are reflected in PIMS.'
      );
    }
  }, [bitwerxStatus, client, client?.bitwerx_client_id, isClientPage]);

  const corporateOptions = useMemo(
    () =>
      getCorporateOptionsByOutcome({
        corporateGroupOptions,
        selectedStep: currentStep,
      }),
    [corporateGroupOptions, currentStep]
  );
  const navigate = useNavigate();
  const { required: stepRequiredFields, fieldsNames: currentStepFieldsNames } =
    useMemo(() => {
      const initial: { required: string[]; fieldsNames: string[] } = {
        required: [],
        fieldsNames: [],
      };
      if (!currentStep) {
        return initial;
      }

      if (
        getShowDepartmentSelect({
          hospital,
          allOutcomes: true,
          selectedStep: currentStep,
        })
      ) {
        initial.fieldsNames.push(getDepartmentsSelectPath(currentStep));
      }

      return (
        currentStep?.widgets?.reduce((accum, widget) => {
          widget?.questions?.forEach((question: FormCardRender) => {
            if (question?.questionFields) {
              accum.fieldsNames = accum.fieldsNames.concat(
                question.questionFields
              );
            }

            const names =
              question?.fields
                ?.reduce<Array<string>>((sum, field) => {
                  sum.push(field?.formFieldProps?.name ?? '');
                  sum.push(field?.checkboxProps?.name ?? '');
                  return sum;
                }, [])
                ?.filter((item: string) => !!item) ?? [];

            accum.fieldsNames = accum.fieldsNames.concat(names);
            accum.required = accum.required.concat(
              question?.requiredFields ?? []
            );
          });
          return accum;
        }, initial) ?? initial
      );
    }, [currentStep]);

  const currentStepFieldValues = useMemo(() => {
    let stateCopy: Record<string, any> = {};

    currentStepFieldsNames.forEach((fieldName) => {
      const pathValue = getValueByPath(values, fieldName);
      stateCopy = fillObjectByPath(stateCopy, pathValue, fieldName);
    });

    return stateCopy;
  }, [values, currentStepFieldsNames]);
  useEffect(() => {
    if (!isShowSinglePageDynamicFlow) {
      return;
    }

    if (!passedSteps?.length || !steps) {
      return;
    }

    // Always base the flow on the first step — Greeting / Call Reason
    const firstStep = passedSteps[0];
    const firstStepData = steps[firstStep?.id];

    if (!firstStepData) {
      return;
    }

    // Recalculate next step based on first step's updated values
    const speciesRuleStepId = getSpeciesStepId({
      values,
      acceptableSpecies,
      speciesRules: firstStepData?.speciesRules,
    });

    const nextRuleStepId =
      speciesRuleStepId ??
      getNextStepId({
        currentStepFieldValues,
        currentStep: firstStepData,
        passedStepsFieldValues: values,
      });

    const nextStep = steps?.[nextRuleStepId];
    const nextOutcomeId = getNextOutcomeId({
      ruleValues,
      outcomeRules: nextStep?.outcomeRules,
    });

    const newNextStepId = nextOutcomeId || nextRuleStepId;
    const currentNextStep = passedSteps[1]; // second step in the list

    const nextStepIsDifferent = currentNextStep?.id !== newNextStepId;
    const hasExtraSteps = passedSteps.length > 1;

    if (nextStepIsDifferent && hasExtraSteps) {
      const trimmedSteps = passedSteps.slice(0, 1);
      setPassedSteps(trimmedSteps);
      setActiveStep(firstStep.id); // stay on first step
    }
  }, [
    currentStep,
    steps,
    activeStepIndex,
    passedSteps,
    acceptableSpecies,
    currentStepFieldValues,
    ruleValues,
    values,
    isShowSinglePageDynamicFlow,
  ]);

  // Track which step was last changed to trigger recalculation
  const [lastChangedStepIndex, setLastChangedStepIndex] = useState<
    number | null
  >(null);

  const handleCurrentStepInputChange = useCallback(
    (index: any) => {
      // setActiveStep(passedSteps[index]?.id);
      // Mark this step as changed - the useEffect will handle the recalculation
      setLastChangedStepIndex(index);
    },
    [setActiveStep, passedSteps]
  );

  // Effect to validate existing steps when form values change after a step input change
  useEffect(() => {
    if (lastChangedStepIndex === null) {
      return;
    }

    const index = lastChangedStepIndex;
    setLastChangedStepIndex(null); // Reset the flag

    // Start from the changed step and validate all subsequent existing steps
    let currentStepIndex = index;
    let validStepCount = index + 1; // Keep all steps up to and including the changed step

    // Iterate through all subsequent existing steps and validate them
    while (currentStepIndex < passedSteps.length - 1) {
      const currentStepId = passedSteps[currentStepIndex]?.id;
      const currentStepData = steps[currentStepId];

      if (!currentStepData) {
        break;
      }

      const speciesRuleStepId = getSpeciesStepId({
        values,
        acceptableSpecies,
        speciesRules: currentStepData?.speciesRules,
      });

      const nextRuleStepId =
        speciesRuleStepId ??
        getNextStepId({
          currentStepFieldValues,
          currentStep: currentStepData,
          passedStepsFieldValues: values, // Use current form values
        });

      const nextStep = steps?.[nextRuleStepId];
      const nextOutcomeId = getNextOutcomeId({
        ruleValues,
        outcomeRules: nextStep?.outcomeRules,
      });
      const nextStepId = nextOutcomeId || nextRuleStepId;

      const actualNextStep = passedSteps[currentStepIndex + 1];

      // Check if the calculated next step matches the actual next step
      if (actualNextStep && actualNextStep.id !== nextStepId) {
        // Flow has changed - this step and all subsequent steps are invalid
        validStepCount = currentStepIndex + 1;
        break;
      }

      // If this step is valid, continue to the next step
      validStepCount = currentStepIndex + 2; // Include the next step
      currentStepIndex++;
    }

    // Update the passed steps to only include valid steps
    if (validStepCount < passedSteps.length) {
      const newPassedSteps = passedSteps.slice(0, validStepCount);
      const lastValidStep = newPassedSteps[newPassedSteps.length - 1];

      setPassedSteps(newPassedSteps);

      // Update activeStep to point to the last valid step
      if (lastValidStep && activeStep !== lastValidStep.id) {
        setActiveStep(lastValidStep.id);
      }
    }
  }, [
    lastChangedStepIndex,
    passedSteps,
    steps,
    values,
    acceptableSpecies,
    currentStepFieldValues,
    ruleValues,
    setPassedSteps,
    activeStep,
    setActiveStep,
  ]);

  const addDefaultClientPet = async () => {
    const clientDefaultValues = {
      phone: '',
      phones: [''],
      addresses: [''],
      name: 'unknown',
      breed: 'unknown',
      gender: 'unknown',
      species: 'unknown',
      last_name: 'unknown',
      first_name: 'unknown',
      is_caller_info_unknown: true,
    };

    try {
      if (
        values?.client_phone &&
        noneFieldName &&
        (values?.client_id === 0 || values.client_id === null) &&
        !isTesting
      ) {
        clientDefaultValues.phone = values?.client_phone;
        clientDefaultValues.phones[0] = values?.client_phone;

        const response: any = await create(
          getPayload(clientDefaultValues, hospitalId!)
        );
        if (response) {
          await dispatch(
            setCurrentTaskClient({
              client: response?.data,
            })
          ).unwrap();
        }
      }
    } catch (error) {
      handleError(
        'An error occurred while creating the client. Please try again.'
      );
    }
  };

  const onNextStep = useCallback(async () => {
    if (currentStep?.isFinal) {
      return;
    }
    const errors = await validateForm(values);
    if (noneFieldName) {
      errors.isNameConfirm = undefined;
      errors.isNumberConfirm = undefined;
    }
    const stepErrors: string[] = [];
    const hasPetInfo =
      stepRequiredFields.some((name) => name.includes('pet.')) &&
      !noneFieldName;
    const removeItems = ['isNameConfirm', 'isNumberConfirm'];

    stepRequiredFields.forEach((requiredName) => {
      const err =
        Object.keys(errors).length && getValueByPath(errors, requiredName);

      if (
        (err &&
          (values?.client_id === 0 || values?.client_id === null) &&
          (!noneFieldName ||
            (noneFieldName && !removeItems.includes(requiredName))) &&
          !isTesting) ||
        (err && isTesting && !removeItems.includes(requiredName))
      ) {
        stepErrors.push(err);
      }
    });
    [
      ...currentStepFieldsNames,
      ...(hasPetInfo ? ['clientPets.0.name'] : []),
    ]?.forEach((name) => {
      if (
        !removeItems.includes(name) ||
        (removeItems.includes(name) &&
          (values.client_id === 0 || values.client_id === null) &&
          !noneFieldName)
      ) {
        setFieldTouched(name, true);
      }
    });
    if (
      stepErrors.length ||
      (hasPetInfo && (errors?.pets || errors?.clientPets))
    ) {
      scrollCaseToError(errors);
      return;
    }
    await addDefaultClientPet();

    const speciesRuleStepId = getSpeciesStepId({
      values,
      acceptableSpecies,
      speciesRules: currentActiveStep?.speciesRules,
    });

    const nextRuleStepId =
      speciesRuleStepId ??
      getNextStepId({
        currentStepFieldValues,
        passedStepsFieldValues: values, // Use current form values instead of stored values
        currentStep: currentActiveStep,
      });
    const nextStep = steps?.[nextRuleStepId];
    const nextOutcomeId = getNextOutcomeId({
      ruleValues,
      outcomeRules: nextStep?.outcomeRules,
    });
    const nextStepId = nextOutcomeId || nextRuleStepId;

    if (steps?.[nextStepId]) {
      if (steps[nextStepId]?.isFinal) {
        setFieldValue('submitRules', currentStep?.submitRules ?? []);
      }

      // Check if the next step already exists in passedSteps
      const nextStepExists = passedSteps.some((step) => step.id === nextStepId);

      if (nextStepExists) {
        // If next step already exists, just navigate to it

        setActiveStep(nextStepId);
      } else {
        // If next step doesn't exist, clean up old journey and add new step

        // Clean up: remove any steps that come after the current step
        // This ensures we don't have old journey steps when going back
        const cleanedPassedSteps = passedSteps.slice(0, activeStepIndex + 1);

        setActiveStep(nextStepId);
        // Use the common processStepData function to process the step
        const processedNextStep = processStepData(steps[nextStepId]);
        setPassedSteps?.([...cleanedPassedSteps, processedNextStep]);
      }
    }
  }, [
    steps,
    values,
    ruleValues,
    setPassedSteps,
    passedSteps,
    activeStepIndex,
    currentActiveStep,
    acceptableSpecies,
    stepRequiredFields,
    currentStep?.isFinal,
    currentStepFieldsNames,
    currentStepFieldValues,
    setFieldValue,
    setActiveStep,
    processStepData,
  ]);

  useEffect(() => {
    if (passedSteps.length > prevPassedStepsLengthRef.current) {
      setTimeout(() => {
        const newStepIndex = passedSteps.length - 1;
        const stepContainers = document.querySelectorAll(
          '[data-step-container]'
        );
        if (stepContainers[newStepIndex]) {
          stepContainers[newStepIndex].scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          });
        }
      }, 100);
    }
    prevPassedStepsLengthRef.current = passedSteps.length;
  }, [passedSteps.length]);

  const onInstruction = useCallback(
    (actionId: string) => {
      const instructionStep = steps[actionId];
      if (instructionStep && activeStepIndex >= 0) {
        const passed = [...passedSteps];
        passed.splice(activeStepIndex, 1, instructionStep);
        setPassedSteps(passed);
        setActiveStep(actionId);
      }
    },
    [steps, passedSteps, activeStepIndex, setPassedSteps, setActiveStep]
  );
  let isSubmitDisabled = false;
  if (
    isEmergency ||
    isAppointment ||
    callOutComeTypes.value === CallOutcomeType.Behavior ||
    callOutComeTypes.value === CallOutcomeType.ShelterClinic ||
    callOutComeTypes.value === CallOutcomeType.ReferToGeneralPractice ||
    callOutComeTypes.value === CallOutcomeType.ReferToEmergency ||
    callOutComeTypes.value === CallOutcomeType.ShelterAdmin
  ) {
    isSubmitDisabled = Boolean(!values.checkbox1);
  }

  useEffect(() => {
    if (activeStepIndex <= passedSteps.length - 1) {
      const idxWithRules = passedSteps.findIndex(
        (stp, indx) => indx >= activeStepIndex && !!stp.nextStep?.rules?.length
      );
      const newPassed = passedSteps.map((passed, idx) => ({
        ...passed,
        hideStep:
          idx > activeStepIndex &&
          passedSteps?.[passedSteps.length - 1].stateCopy !==
            JSON.stringify(shallowDifference(initialValues, values)) &&
          idxWithRules >= 0 &&
          idx > idxWithRules,
      }));
      setPassedSteps(newPassed);
    }
  }, [values]);

  const showNewClient = useMemo(() => {
    const hasClientWidget = !!currentStep?.widgets?.some(
      (widget) => !!(widget?.widgetType === FormCardTypes.ClientInfo)
    );
    return hasClientWidget && !isTesting && !values?.client_id;
  }, [values?.client_id, currentStep?.widgets, isTesting]);

  useEffect(() => {
    const hasErValidation = stepQuestions.some(
      (question) => question.validationType === ValidationType.ReferToER
    );
    const hasOnCallValidation = stepQuestions.some(
      (question) => question.validationType === ValidationType.ReferToOnCall
    );
    const names = widgetData[FormCardTypes.ReferToER]?.requiredFields;
    const onCallNames = widgetData[FormCardTypes.OnCallDoc]?.requiredFields;

    setValidationFields({
      [ValidationType.ReferToER]: hasErValidation ? names : [],
      [ValidationType.ReferToOnCall]: hasOnCallValidation ? onCallNames : [],
    });
  }, [activeStep, JSON.stringify(widgetData)]);

  useEffect(() => {
    if (outcome && subOutcome && hospital?.type !== 2) {
      setFieldValue('callOutcome.type', outcome);
      setFieldValue(`callOutcome.${outcome}.type`, subOutcome);
    }
  }, [outcome, subOutcome]);

  const { isButtonHide } = useSelector(
    (state: RootState) => state.analyticsExport
  );

  const isEditFeedbackForm = location.pathname.includes('consultation/edit');
  const isActionCenterURL = location.pathname.includes('action-center');
  const isDynamicFlowOrderChanged = process.env.REACT_APP_CHANGE_DYNAMIC_ORDER;

  const isHideBreed = isDynamicFlowOrderChanged
    ? JSON.parse(isDynamicFlowOrderChanged).some(
        (item: any) => item == hospitalId
      )
    : false;

  const callFlowInProgress =
    localStorage.getItem('callFlowInProgress') === 'true';

  const { disconnected, disconnected_at } = task ?? {};

  const hasInQueue = useAppSelector(selectHasInQueue(task));

  const { isLostDialogVisible } = useConnectionState(task);

  const isTransferToBackline =
    hasInQueue &&
    (isLostDialogVisible || (disconnected && disconnected_at)) &&
    !callFlowInProgress;

  // Handle Tab key event for "Next" button
  const handleKeyDown = (e: any) => {
    if (e.key === 'Tab') {
      const activeElement: any = document.activeElement;
      if (
        activeElement &&
        activeElement.tagName === 'BUTTON' &&
        activeElement.innerText === 'Next'
      ) {
        e.preventDefault();
        onNextStep();
      }
    }
  };
  return (
    <>
      {isShowSinglePageDynamicFlow ? (
        <Styles.SinglePageContainer
          tabIndex={0}
          onKeyDown={handleKeyDown}
          style={{ outline: 'none' }}
        >
          {passedSteps?.map((currentSteps, index) => (
            <Styles.SinglePageMain
              data-step-container
              key={`${currentSteps.id}-${index}`}
            >
              <Styles.SinglePageStyledList
                count={1}
                isLoading={isLoading}
                mainWrapperStyles={{
                  gap: '24px',
                  display: 'block',
                  overflow: 'visible',
                  padding: `0 ${mobile ? 20 : isChatType ? 60 : 120}px 16px`,
                }}
                header={
                  <Styles.Header mobile={mobile} isChat={isChatType}>
                    {currentSteps?.title ===
                    'Greeting and call reason' ? null : (
                      <h2>
                        {currentSteps?.title === 'Submit case'
                          ? ' Select call outcome'
                          : currentSteps?.title}
                      </h2>
                    )}
                    {currentSteps?.script && (
                      <Styles.Script>{currentSteps?.script}</Styles.Script>
                    )}
                  </Styles.Header>
                }
              >
                {index === 0 && (
                  <Grid container spacing={2}>
                    <Grid item md={6} xl={9} lg={7} sm={12}>
                      <Box mt={2} mb={2}>
                        <Styles.ParaDynamic2>
                          {currentSteps?.widgets?.[0]?.questions?.[0]
                            ?.subtitle ? (
                            <Box
                              sx={{ whiteSpace: 'pre-wrap' }}
                              dangerouslySetInnerHTML={{
                                __html:
                                  currentSteps?.widgets[0]?.questions[0]?.subtitle?.replace(
                                    /\n/g,
                                    '<br />'
                                  ),
                              }}
                            />
                          ) : (
                            <Box
                              sx={{ whiteSpace: 'pre-wrap' }}
                              dangerouslySetInnerHTML={{
                                __html:
                                  currentSteps?.widgets?.[2]?.questions?.[0]?.subtitle?.replace(
                                    /\n/g,
                                    '<br />'
                                  ),
                              }}
                            />
                          )}
                        </Styles.ParaDynamic2>
                      </Box>
                    </Grid>
                    <Grid item xl={3} lg={5} md={6} sm={12}>
                      <Box
                        mt={2}
                        mb={2}
                        display="flex"
                        justifyContent="flex-end"
                      >
                        {isActionCenterURL &&
                          bitwerxStatus &&
                          client?.bitwerx_client_id && (
                            <>
                              <StandardButton
                                text="View PIMS Details"
                                onClick={() => {
                                  navigate(
                                    `${basePath}${Routes.ActionCenter.InComm.BitwerxDetail}`.replace(
                                      ':clientId',
                                      client?.bitwerx_client_id
                                    ) + ''
                                  );
                                }}
                              />
                              <StandardButton
                                text="Edit Client Info"
                                style={{ marginLeft: '10px' }}
                                onClick={() => {
                                  navigate(
                                    `${basePath}${Routes.ActionCenter.InComm.EditClient}`.replace(
                                      ':clientId',
                                      client?.bitwerx_client_id || ''
                                    ) + `?clientId=${client?.bitwerx_client_id}`
                                  );
                                }}
                              />
                            </>
                          )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
                {index === 0 && (
                  <GreetingInfo
                    isDynamicFlow={true}
                    complaintsValue={values.chief_complaints}
                  />
                )}
                {index === 0 && bitwerxStatus && client?.bitwerx_client_id && (
                  <>
                    <PimsInfo client={client} />
                  </>
                )}
                {showNewClient && noneFieldName ? (
                  <>
                    <ClientField
                      noneFieldName={noneFieldName}
                      setFieldTouched={setFieldTouched}
                      task={task ?? ({} as ActionQueue)}
                      setNoneFieldName={setNoneFieldName}
                    />
                  </>
                ) : null}
                {showNewClient && !noneFieldName ? (
                  <ClientField
                    noneFieldName={noneFieldName}
                    setFieldTouched={setFieldTouched}
                    task={task ?? ({} as ActionQueue)}
                    setNoneFieldName={setNoneFieldName}
                  />
                ) : currentSteps?.isFinal ? (
                  <SubmitStep />
                ) : (
                  <FlexLayout flexDirection="column">
                    {showDepartmentSelect && (
                      <DepartmentSelect
                        options={corporateOptions}
                        isDisabled={isCorporateGroupFetching}
                        name={getDepartmentsSelectPath(currentStep)}
                      />
                    )}
                    {currentSteps?.widgets?.map(
                      (
                        { id, questions, widgetType, ...rest }: any,
                        indx: any
                      ) => {
                        const additionalData = widgetType
                          ? (widgetData[widgetType] ?? {})
                          : {};
                        let mergedRest = { ...rest, ...additionalData };
                        if (
                          (id === 'clientInfo' || id === 'petInfo') &&
                          (values.client_id === 0 ||
                            values.client_id === null) &&
                          !isTesting
                        ) {
                          return null;
                        }
                        if (
                          (id === 'clientInfo' ||
                            widgetType === FormCardTypes.ClientInfo) &&
                          values?.client_id &&
                          values.client_id !== 0 &&
                          values.client_id !== null &&
                          bitwerxStatus &&
                          client?.bitwerx_client_id
                        ) {
                          return null;
                        }
                        return (
                          <FormCard
                            readOnly
                            key={indx}
                            isEdit={isEdit}
                            isTesting={isTesting}
                            formName={widgetType}
                            onNextClick={onNextStep}
                            onPlaceBack={onPlaceBack}
                            isHideBreed={isHideBreed}
                            isDisablePets={isChatType}
                            isBitwerxEmabled={bitwerxStatus}
                            isEditDisable={hospital?.type === 2}
                            petIds={task?.petIds ?? values?.pets}
                            hospitalType={Number(hospital?.type)}
                            onDeceasedChange={(payload) => {
                              if (values?.client_id && !isTesting) {
                                updatePet({
                                  ...payload,
                                  client_id: Number(values.client_id),
                                });
                              }
                            }}
                            paymentLinksProps={{
                              isInlineGrid: true,
                              grid: {
                                gap: '16px',
                                template: 'auto / auto',
                              },
                              onSendLinks: () => {
                                setPaymentLinks?.({
                                  textOutStep: TextOutStep.TemplateInfo,
                                  petOwnerTemplate:
                                    PetOwnerTemplate.PaymentLinks,
                                  selectedRecipientType:
                                    MessageRecipientType.PetOwner,
                                });
                              },
                            }}
                            transferToBacklineProps={{
                              transfers: rest?.transfers ?? [],
                              onClick: () => {
                                if (!isTransferToBackline) {
                                  const phoneNumber = rest?.transferToBLNumber;
                                  if (
                                    phoneNumber &&
                                    (onTransferMerge as any)?.setTransferPhone
                                  ) {
                                    (onTransferMerge as any).setTransferPhone(
                                      phoneNumber
                                    );
                                  }

                                  onTransferMerge?.(true);
                                }
                              },
                            }}
                            render={(index === 0
                              ? questions.map((question: any) =>
                                  indx === 0
                                    ? { ...question, subtitle: '' }
                                    : question
                                )
                              : questions
                            ).map((question: any) => {
                              const isClient =
                                question.validationType ===
                                ValidationType.ClientInfo;
                              const isPet =
                                question.validationType ===
                                ValidationType.PetInfo;
                              return {
                                ...question,
                                requiredFields: [
                                  ...(question?.requiredFields ?? []),
                                  ...(additionalData.requiredFields ?? []),
                                ],
                                addPath: isEdit
                                  ? ''
                                  : isClient
                                    ? editClientPath
                                    : isPet && hospital?.type !== 2
                                      ? addPetPath
                                      : '',
                                fields: question.fields?.map((field: any) => {
                                  const fieldWithOptions =
                                    getFieldOptions(field);
                                  return {
                                    ...fieldWithOptions,
                                    formFieldProps: {
                                      ...fieldWithOptions.formFieldProps,
                                      ...(fieldWithOptions.type ===
                                      'buttonsGroup'
                                        ? {
                                            onClick: () => {
                                              handleCurrentStepInputChange(
                                                index
                                              );
                                            },
                                          }
                                        : {
                                            onChange: () => {
                                              handleCurrentStepInputChange(
                                                index
                                              );
                                            },
                                          }),
                                    },
                                  };
                                }),
                              };
                            })}
                            {...mergedRest}
                          />
                        );
                      }
                    )}
                  </FlexLayout>
                )}
                <StepInstructions
                  steps={steps}
                  onClick={onInstruction}
                  instructions={currentSteps?.instructions}
                />
              </Styles.SinglePageStyledList>
            </Styles.SinglePageMain>
          ))}
          {!isLoading && !hideFooter && (
            <Styles.SinglePageFooter
              isChat={isChatType}
              // left={
              //   activeStepIndex > 0 ? (
              //     <Styles.BackButton
              //       icon={<Icon.ChevronLeft />}
              //       colorType={ButtonColors.Secondary}
              //       onClick={() =>
              //         setActiveStep(passedSteps[activeStepIndex - 1]?.id)
              //       }
              //     />
              //   ) : undefined
              // }
              right={
                isSubmitStep ? (
                  <StandardButton
                    text="Submit"
                    colorType={ButtonColors.LightGreen}
                    disabled={
                      isEditFeedbackForm
                        ? isSubmitting
                        : isButtonHide || isSubmitDisabled
                    }
                    onClick={() => {
                      if (isTesting) {
                        resetForm();
                      } else if (onSubmitPress) {
                        onSubmitPress();
                      } else {
                        submitForm();
                      }
                    }}
                  />
                ) : (
                  <StandardButton
                    text="Next"
                    onClick={onNextStep}
                    disabled={
                      values?.problem_summary === null ||
                      values?.chief_complaints?.length === 0 ||
                      (isClientPage &&
                        hospitalEditStatus !== true &&
                        client &&
                        !client?.bitwerx_client_id &&
                        bitwerxStatus)
                      // isSubmitting || showNewClient
                    }
                  />
                )
              }
            />
          )}
        </Styles.SinglePageContainer>
      ) : (
        <Styles.Container>
          {/* <Aside
          isLoading={isLoading}
          activeStep={activeStep}
          passedSteps={passedSteps}
          setActiveStep={setActiveStep}
          setPassedSteps={setPassedSteps}
          stepsToReplace={stepsToReplace}
        /> */}

          <Styles.Main>
            <Styles.StyledList
              count={1}
              isLoading={isLoading}
              mainWrapperStyles={{
                gap: '24px',
                display: 'flex',
                flexDirection: 'column',
                padding: `0 ${mobile ? 20 : isChatType ? 60 : 120}px 16px`,
              }}
              header={
                <Styles.Header mobile={mobile} isChat={isChatType}>
                  {currentStep?.title === 'Greeting and call reason' ? null : (
                    <h2>
                      {currentStep?.title === 'Submit case'
                        ? ' Select call outcome'
                        : currentStep?.title}
                    </h2>
                  )}
                  {currentStep?.script && (
                    <Styles.Script>{currentStep?.script}</Styles.Script>
                  )}
                </Styles.Header>
              }
            >
              <>
                {activeStepIndex === 0 && (
                  <Grid container spacing={2}>
                    <Grid item md={6} xl={9} lg={7} sm={12}>
                      <Box mt={2}>
                        <Styles.ParaDynamic2>
                          {currentStep?.widgets?.[0]?.questions?.[0]
                            ?.subtitle ? (
                            <Box
                              sx={{ whiteSpace: 'pre-wrap' }}
                              dangerouslySetInnerHTML={{
                                __html:
                                  currentStep?.widgets[0]?.questions[0]?.subtitle?.replace(
                                    /\n/g,
                                    '<br />'
                                  ),
                              }}
                            />
                          ) : (
                            <Box
                              sx={{ whiteSpace: 'pre-wrap' }}
                              dangerouslySetInnerHTML={{
                                __html:
                                  currentStep?.widgets?.[2]?.questions?.[0]?.subtitle?.replace(
                                    /\n/g,
                                    '<br />'
                                  ),
                              }}
                            />
                          )}
                        </Styles.ParaDynamic2>
                      </Box>
                    </Grid>
                    <Grid item xl={3} lg={5} md={6} sm={12}>
                      <Box mt={2} display="flex" justifyContent="flex-end">
                        {isActionCenterURL &&
                          bitwerxStatus &&
                          client?.bitwerx_client_id && (
                            <>
                              <StandardButton
                                text="View PIMS Details"
                                onClick={() => {
                                  navigate(
                                    `${basePath}${Routes.ActionCenter.InComm.BitwerxDetail}`.replace(
                                      ':clientId',
                                      client?.bitwerx_client_id
                                    ) + ''
                                  );
                                }}
                              />
                              <StandardButton
                                text="Edit Client Info"
                                style={{ marginLeft: '10px' }}
                                onClick={() => {
                                  navigate(
                                    `${basePath}${Routes.ActionCenter.InComm.EditClient}`.replace(
                                      ':clientId',
                                      client?.bitwerx_client_id || ''
                                    ) + `?clientId=${client?.bitwerx_client_id}`
                                  );
                                }}
                              />
                            </>
                          )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
                {activeStepIndex === 0 && (
                  <GreetingInfo
                    isDynamicFlow={true}
                    complaintsValue={values.chief_complaints}
                  />
                )}
                {activeStepIndex === 0 &&
                  bitwerxStatus &&
                  client?.bitwerx_client_id && (
                    <>
                      <PimsInfo client={client} />
                    </>
                  )}
                {showNewClient && noneFieldName ? (
                  <>
                    <ClientField
                      noneFieldName={noneFieldName}
                      setFieldTouched={setFieldTouched}
                      task={task ?? ({} as ActionQueue)}
                      setNoneFieldName={setNoneFieldName}
                    />
                  </>
                ) : null}
                {showNewClient && !noneFieldName ? (
                  <ClientField
                    noneFieldName={noneFieldName}
                    setFieldTouched={setFieldTouched}
                    task={task ?? ({} as ActionQueue)}
                    setNoneFieldName={setNoneFieldName}
                  />
                ) : isSubmitStep ? (
                  <SubmitStep />
                ) : (
                  <FlexLayout flexDirection="column">
                    {showDepartmentSelect && (
                      <DepartmentSelect
                        options={corporateOptions}
                        isDisabled={isCorporateGroupFetching}
                        name={getDepartmentsSelectPath(currentStep)}
                      />
                    )}

                    {currentStep?.widgets?.map(
                      ({ id, questions, widgetType, ...rest }, indx) => {
                        if (
                          (id === 'clientInfo' || id === 'petInfo') &&
                          (values.client_id === 0 ||
                            values.client_id === null) &&
                          !isTesting
                        ) {
                          return;
                        }
                        if (
                          (id === 'clientInfo' ||
                            widgetType === FormCardTypes.ClientInfo) &&
                          values?.client_id &&
                          values.client_id !== 0 &&
                          values.client_id !== null &&
                          bitwerxStatus &&
                          client?.bitwerx_client_id
                        ) {
                          return;
                        }
                        return (
                          <FormCard
                            readOnly
                            key={indx}
                            isEdit={isEdit}
                            formName={widgetType}
                            isTesting={isTesting}
                            onNextClick={onNextStep}
                            onPlaceBack={onPlaceBack}
                            isHideBreed={isHideBreed}
                            isDisablePets={isChatType}
                            isBitwerxEmabled={bitwerxStatus}
                            isEditDisable={hospital?.type === 2}
                            petIds={task?.petIds ?? values?.pets}
                            hospitalType={Number(hospital?.type)}
                            onDeceasedChange={(payload) => {
                              if (values?.client_id && !isTesting) {
                                updatePet({
                                  ...payload,
                                  client_id: Number(values.client_id),
                                });
                              }
                            }}
                            render={
                              activeStepIndex === 0
                                ? questions.map((question) =>
                                    indx === 0
                                      ? { ...question, subtitle: '' }
                                      : question
                                  )
                                : questions
                            }
                            paymentLinksProps={{
                              isInlineGrid: true,
                              grid: {
                                gap: '16px',
                                template: 'auto / auto',
                              },
                              onSendLinks: () => {
                                setPaymentLinks?.({
                                  textOutStep: TextOutStep.TemplateInfo,
                                  petOwnerTemplate:
                                    PetOwnerTemplate.PaymentLinks,
                                  selectedRecipientType:
                                    MessageRecipientType.PetOwner,
                                });
                              },
                            }}
                            transferToBacklineProps={{
                              transfers: rest?.transfers ?? [],
                              onClick: () => {
                                if (!isTransferToBackline) {
                                  const phoneNumber = rest?.transferToBLNumber;
                                  if (
                                    phoneNumber &&
                                    (onTransferMerge as any)?.setTransferPhone
                                  ) {
                                    (onTransferMerge as any).setTransferPhone(
                                      phoneNumber
                                    );
                                  }

                                  onTransferMerge?.(true);
                                }
                              },
                            }}
                            {...rest}
                          />
                        );
                      }
                    )}
                  </FlexLayout>
                )}
                <StepInstructions
                  steps={steps}
                  onClick={onInstruction}
                  instructions={currentStep?.instructions}
                />
              </>
            </Styles.StyledList>

            {!isLoading && !hideFooter && (
              <Styles.Footer
                isChat={isChatType}
                left={
                  activeStepIndex > 0 ? (
                    <Styles.BackButton
                      icon={<Icon.ChevronLeft />}
                      colorType={ButtonColors.Secondary}
                      onClick={() =>
                        setActiveStep(passedSteps[activeStepIndex - 1]?.id)
                      }
                    />
                  ) : undefined
                }
                right={
                  isSubmitStep ? (
                    <StandardButton
                      text="Submit"
                      colorType={ButtonColors.LightGreen}
                      disabled={
                        isEditFeedbackForm
                          ? isSubmitting
                          : isButtonHide || isSubmitDisabled
                      }
                      onClick={() => {
                        if (isTesting) {
                          resetForm();
                        } else if (onSubmitPress) {
                          onSubmitPress();
                        } else {
                          submitForm();
                        }
                      }}
                    />
                  ) : (
                    <StandardButton
                      text="Next"
                      onClick={onNextStep}
                      disabled={
                        values?.problem_summary === null ||
                        values?.chief_complaints?.length === 0 ||
                        (isClientPage &&
                          hospitalEditStatus !== true &&
                          client &&
                          !client?.bitwerx_client_id &&
                          bitwerxStatus)
                        // isSubmitting || showNewClient
                      }
                    />
                  )
                }
              />
            )}
          </Styles.Main>
        </Styles.Container>
      )}
    </>
  );
};
